import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { json } from 'd3';
import { geoConicConformal, geoPath } from 'd3-geo';
import { select } from 'd3-selection';
import './querybuilder.scss';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import * as Icon from 'react-feather';
import DatawrapperDownloads from '../datawrapper/datawrapperDownloads.jsx';
import colors from '../../../../../../../../apps/baykis-pl/pattern-lab/_data/generated_colors.json';
import config from './config.json';

const apiQuery = [];
// TODO: remove later
const host = window.location.hostname;
const prefix =
  host.includes('patternlab') ||
  host.includes('styleguide') ||
  host === 'dev.baykis.test' ||
  (host.includes('localhost') && !host.includes('drupal'))
    ? ''
    : '/themes/baykis/dist/app-drupal';

const shapeURL = `${prefix}/assets/statics/data/baykis_bayern_geo.json`;
let activeRegionBuffer = 11;

const selectbox = {
  fileFormats: [
    { label: 'netCDF', value: 'nc' },
    { label: 'CSV', value: 'csv' },
  ],
  regions: [
    { label: 'Bayern', value: 'bavaria' },
    { label: 'Regierungsbezirke', value: 'govregion' },
    { label: 'Landkreise', value: 'county' },
    { label: 'Klimaregionen', value: 'climaregion' },
    { label: 'Naturräume', value: 'nature' },
  ],
  epochs: [
    { label: 'Daten der Zukunft', value: 'future' },
    { label: 'Daten der Vergangenheit', value: 'past' },
  ],
};

class QueryBuilderDownloads extends Component {
  bayernShape;

  geopath;

  constructor(props) {
    super(props);
    this.regions = React.createRef();
    this.minimapSvgRef = React.createRef();
    this.fileFormat = React.createRef();
    this.epoch = React.createRef();
    const searchParams = new URLSearchParams(window.location.search);

    this.state = {
      activeRegion: parseInt(searchParams.get('region'), 10),
      regionName: searchParams.get('regionName'),
      regionPartitioning: searchParams.get('regionPartitioning'),
      regionPartitioningValue: searchParams.get('regionPartValue'),
      fileFormat: searchParams.get('format') || 'nc',
      epoch: searchParams.get('epoch'),
    };
  }

  componentDidMount() {
    const searchParams = new URLSearchParams(window.location.search);
    this.buildMinimap(searchParams.get('regionPartValue') || 'bavaria');
  }

  changeRegion = (event) => {
    const regionPartitioningValue = event.currentTarget.value;
    const regionPartitioning = selectbox.regions.find((regionGroupObject) => {
      return regionGroupObject.value === regionPartitioningValue;
    });

    this.buildMinimap(regionPartitioningValue);

    this.setState({
      regionName: '',
      regionPartitioning: regionPartitioning.label,
      regionPartitioningValue,
    });
  };

  selectRegionOnMap = (activeNode, regionName) => {
    const selection = select('path.regionActive');
    const selectedNode = selection.node();

    selection.style('fill', colors.themeBase.box);
    selectedNode?.classList.remove('regionActive');

    if (activeNode && activeNode.getAttribute('dataname') === regionName) {
      const newSelection = select(activeNode);

      newSelection.attr('class', 'regionActive');
      newSelection.style('fill', colors.themeBase.accentDark);
    }
  };

  buildMinimap = (region) => {
    // GeoJSON Features Indizes von bis der jeweiligen Region
    const regionConfig = {
      bavaria: { idFrom: 11, idTo: 11 },
      govregion: { idFrom: 12, idTo: 18 },
      county: { idFrom: 19, idTo: 47 },
      climaregion: { idFrom: 119, idTo: 125 },
      nature: { idFrom: 1, idTo: 4 },
      default: { idFrom: 11, idTo: 11 },
    };
    const { idFrom, idTo } = regionConfig[region];
    const svgWidth = 273;
    const svgHeight = 300;
    const svgPaddingTop = 15;
    const svgPaddingBottom = 25;

    // Je nach ausgewählter Region werden hier nur die zu betrachtenden Features hinein gefügt
    let filteredShape;
    const filterShapes = () => {
      const filtered = this.bayernShape.features.filter(
        (feature) =>
          feature.properties.OBJECTID >= idFrom &&
          feature.properties.OBJECTID <= idTo
      );
      // DeepCopy der Ursprungsdatei
      filteredShape = JSON.parse(JSON.stringify(filtered));
      // TODO Marc prüfen, welches winding der Geometrie vorliegt, damit das reverse & DeepCopy erspart bleibt
      // Momentan Umkehren des Koordinaten Arrays, da d3 die Koordinaten im Uhrzeigersinn erwartet, was
      // konträr zum RFC 7946 steht
      filteredShape.forEach((elem) => elem.geometry.coordinates[0].reverse());
    };

    /**
     * Anhand des geopath werden aus Koordinaten svg Zeichenbefehle generiert
     */
    const buildGeoPath = () => {
      // Projektion anhand von ganz Bayern - Bayern gesamt muss an Index 10 liegen
      // Für Minimap ist hier statisch Index 10 Interessant, da nicht an Features "gezoomt" werden soll
      const bayernShapeReversed = JSON.parse(
        JSON.stringify(this.bayernShape.features[10])
      );
      bayernShapeReversed.geometry.coordinates[0].reverse();
      const projection = geoConicConformal()
        .parallels([35, 65])
        .rotate([-10, 0])
        .fitSize(
          [svgWidth, svgHeight - svgPaddingTop - svgPaddingBottom],
          bayernShapeReversed
        );

      this.geopath = geoPath(projection);
    };

    /**
     * @returns DOM-Node von SVG
     */
    const createSVG = () => {
      const svgNode = this.minimapSvgRef.current;
      const svgSelection = select(svgNode);

      if (!svgNode.querySelector('defs')) {
        svgSelection.attr('viewBox', `0 0 ${svgWidth} ${svgHeight}`);

        const defs = svgSelection.append('defs');

        defs
          .append('path')
          .attr('class', 'outline')
          .attr('d', this.geopath({ type: 'Sphere' }));

        defs
          .append('clipPath')
          .attr('class', 'clip')
          .append('use')
          .attr('href', new URL('#outline', window.location));

        svgSelection.append('g');
      } else {
        // Entferne alte Regionen aus der Minimap
        svgSelection.selectAll('path').remove();
      }
      return svgSelection;
    };

    const drawMinimap = () => {
      const svg = createSVG();

      // Das Layer des SVGs, auf dem die Regionen gezeichnet werden sollen
      const regionsLayer = svg.select('g');

      regionsLayer
        .selectAll('path')
        .data(filteredShape)
        .enter()
        .append('path')
        .attr('d', this.geopath)
        .attr('dataname', (d) => d.properties.Name)
        .attr('dataid', (d) => d.properties.OBJECTID)
        .attr('id', (d) => d.properties.OBJECTID)
        .attr('transform', `translate(0, ${svgPaddingTop})`)
        .on('click', (event) => {
          const regionName = event.target.attributes.dataname.value;

          this.selectRegionOnMap(event.target, regionName);

          apiQuery[4] = parseInt(event.target.attributes.dataid.value, 10);
          activeRegionBuffer = parseInt(
            event.target.attributes.dataid.value,
            10
          );

          this.setState({
            regionName,
          });
        })
        .on('mouseover', (event) => {
          select(event.target).style('fill', colors.themeBase.accentDark);
        })
        .on('mouseout', (event) => {
          select(event.target).style('fill', colors.themeBase.box);
          select('path.regionActive').style(
            'fill',
            colors.themeBase.accentDark
          );
        })
        .style('fill', colors.themeBase.box)
        .style('stroke', colors.themeBase.accentDark)
        .style('stroke-linejoin', 'round')
        .style('stroke-width', '1px');

      const { activeRegion, regionName } = this.state;

      if (activeRegion) {
        this.selectRegionOnMap(
          document.querySelector(`path[dataid="${activeRegion}"]`),
          regionName
        );
      }
    };

    /**
     * Filtern der Regionen und neu zeichnen der Karte
     */
    const redraw = () => {
      filterShapes();
      drawMinimap();
    };

    // Bayern Shape als Singleton - kein mehrfaches laden der JSON
    if (!this.bayernShape) {
      json(shapeURL).then((shape) => {
        this.bayernShape = shape;
        this.setState({
          fullShape: this.bayernShape,
          filteredShape: [this.bayernShape.features[10]],
        });
        // Initiales bauen des geopath
        buildGeoPath();
        redraw();
      });
    } else {
      redraw();
    }
  };

  useFilter = (view = null) => {
    this.setState({
      fileFormat: this.fileFormat.current.value,
      epoch: this.epoch.current.value,
      activeRegion: activeRegionBuffer,
    });
  };

  render() {
    const { activeRegion, regionName, regionPartitioning, regionPartitioningValue, fileFormat, epoch } =
      this.state;

    const { apiUrl } = this.props;

    return (
      <div className="grid -gutter">
        <div className="filter grid__bit -w3-tablet">
          <h3>Filter</h3>
          <label htmlFor="epoch">
            {config.filters.epoch.title}
            <Tippy content={config.filters.epoch.description} interactive>
              <span className="icon">
                <Icon.Info className="feather" />
              </span>
            </Tippy>
            <select name="epoch" ref={this.epoch}>
              {selectbox.epochs.map((option) => {
                return (
                  <option
                    selected={option.value === epoch}
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                );
              })}
            </select>
          </label>
          <label htmlFor="regions">
            {config.filters.region.title}
            <Tippy content={config.filters.region.description} interactive>
              <span className="icon">
                <Icon.Info className="feather" />
              </span>
            </Tippy>
            <select
              name="regions"
              ref={this.region}
              onChange={this.changeRegion}
            >
              {selectbox.regions.map((option) => {
                return (
                  <option
                    selected={option.value === regionPartitioningValue}
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                );
              })}
            </select>
          </label>
          <div className="minimap-container">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="minimapShape"
              id="minimapShape"
              ref={this.minimapSvgRef}
            />
            <label htmlFor="minimapShape" className="nameRegion">
              {regionName}
            </label>
          </div>
          <label htmlFor="fileFormats">
            {config.filters.fileFormats.title}
            <Tippy content={config.filters.fileFormats.description} interactive>
              <span className="icon">
                <Icon.Info className="feather" />
              </span>
            </Tippy>
            <select
              name="fileFormats"
              ref={this.fileFormat}
              onBlur={(e) => {
                this.setState({ fileFormat: e.currentTarget.value });
              }}
            >
              {selectbox.fileFormats.map((option) => {
                return (
                  <option
                    selected={option.value === fileFormat}
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                );
              })}
            </select>
          </label>
          <button
            type="button"
            className="useFilter"
            onClick={() => this.useFilter()}
            disabled={!regionName}
          >
            <Icon.Filter className="feather" />
            Dateien anzeigen
          </button>
        </div>
        {activeRegion ? (
          <DatawrapperDownloads
            activeRegion={activeRegion}
            regionName={regionName}
            fileFormat={fileFormat}
            regionPartitioning={regionPartitioning}
            epoch={epoch}
            apiUrl={apiUrl}
          />
        ) : (
          <div className="datawrapper grid__bit -w9-tablet">
            <div className="download-wrapper">
              <p>
                Bitte wählen Sie die Art der Daten, die gewünschte Region sowie
                das Datenformat aus. Die Klimadaten stehen für die Vergangenheit
                und die Zukunft zur Verfügung. Es können Daten für verschiedene
                Regionen wie das Bundesland Bayern, die bayerischen
                Regierungsbezirke, Landkreise, Klimaregionen und Naturräume
                bezogen werden. Die Daten im NetCDF-Format beziehen sich auf die
                Kartendarstellungen und die Daten im CSV-Format auf die
                Zeitreihendarstellungen. Eine ausführliche Beschreibung der
                Datensätze wird beim Datendownload mitgeliefert.
              </p>
              <p>
                Mit dem Download der Daten stimmen Sie den
{' '}
                <a href="/impressum">Nutzungsbedingungen</a>
{' '}
und der{' '}
                <a href="/datenschutz">Datenschutzerklärung</a>
{' '}
zu.
</p>
            </div>
          </div>
        )}
      </div>
    );
  }
}

QueryBuilderDownloads.propTypes = {
  apiEndpoint: PropTypes.string,
};

QueryBuilderDownloads.defaultProps = {
  apiEndpoint: '',
};

export default QueryBuilderDownloads;
